import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Rocket, Plus, Loader2, Bot } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  live: 'bg-green-100 text-green-700',
  approved: 'bg-green-100 text-green-700',
  plan_ready: 'bg-amber-100 text-amber-700',
  planning: 'bg-blue-100 text-blue-700',
  auditing: 'bg-blue-100 text-blue-700',
  draft: 'bg-muted text-muted-foreground',
};

const statusLabels = {
  live: 'Draait',
  approved: 'Goedgekeurd',
  plan_ready: 'Plan klaar',
  planning: 'Plan opstellen',
  auditing: 'Scannen',
  draft: 'Nieuw',
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Fout bij ophalen projecten');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name || !newProject.description) {
      toast.error('Vul alle velden in');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/projects`, newProject);
      toast.success('Project aangemaakt!');
      setOpenDialog(false);
      setNewProject({ name: '', description: '' });
      navigate(`/project/${response.data.id}`);
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Fout bij aanmaken project');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-background">
      {/* Header */}
      <header className="border-b bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary rounded-lg flex items-center justify-center">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">laibel.io</h1>
              <p className="text-xs text-muted-foreground">Vindbaar, draaiend en groeiend — op één plek.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate('/studio')} data-testid="studio-nav-btn">
            <Bot className="w-4 h-4" />
            Studio
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Action Bar */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Jouw apps</h2>
            <p className="text-muted-foreground mt-1">Vindbaar, draaiend en groeiend — op één plek</p>
          </div>
          <Dialog open={openDialog} onOpenChange={setOpenDialog}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-project-btn">
                <Plus className="w-4 h-4" />
                Activeer je app
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-project-dialog">
              <DialogHeader>
                <DialogTitle>Activeer je app</DialogTitle>
                <DialogDescription>
                  Je app staat al live. laibel scant 'm, maakt een plan en houdt 'm draaiend. Straks vraagt de wizard om je domein.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Naam van je app</Label>
                  <Input
                    id="name"
                    placeholder="Bijv. Lesvoorbereiden.online"
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    data-testid="project-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Wat doet je app? Voor wie?</Label>
                  <Textarea
                    id="description"
                    placeholder="Bijv. Lesvoorbereiding voor basisschoolleerkrachten — in minuten een complete les."
                    rows={3}
                    value={newProject.description}
                    onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                    data-testid="project-description-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenDialog(false)}>Annuleer</Button>
                <Button onClick={handleCreateProject} disabled={creating} data-testid="create-project-submit">
                  {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Aanmaken
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : projects.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <Rocket className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Nog geen apps</h3>
              <p className="text-muted-foreground text-center mb-6 max-w-sm">
                Koppel je eerste app. laibel scant 'm, maakt 'm vindbaar en houdt 'm draaiend.
              </p>
              <Button onClick={() => setOpenDialog(true)} data-testid="empty-state-create-btn">
                <Plus className="w-4 h-4 mr-2" />
                Activeer je eerste app
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card
                key={project.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/project/${project.id}`)}
                data-testid={`project-card-${project.id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg">{project.name}</CardTitle>
                    <Badge className={statusColors[project.activation_status] || statusColors.draft} variant="secondary">
                      {statusLabels[project.activation_status] || statusLabels.draft}
                    </Badge>
                  </div>
                  <CardDescription className="line-clamp-2">{project.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground">
                    Aangemaakt: {new Date(project.created_at).toLocaleDateString('nl-NL')}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}